import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Check, CheckCheck, X, AlertCircle,
  Wrench, DollarSign, FileText, Home, Calendar,
  MessageSquare, ClipboardCheck, AlertTriangle, Info
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDismissNotification,
  type AppNotification,
} from '../hooks/useNotifications';

export default function NotificationCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: notifications = [], isLoading: loading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const dismiss = useDismissNotification();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const markAsRead = (id: number) => markRead.mutate(id);
  const markAllAsRead = () => markAllRead.mutate();
  const dismissNotification = (id: number) => dismiss.mutate(id);

  const getNotificationIcon = (type: string) => {
    switch (type?.toUpperCase()) {
      case 'RENT_REMINDER':
      case 'PAYMENT_DUE':
      case 'PAYMENT_RECEIVED':
      case 'PAYMENT_FAILED':
        return <DollarSign className="w-5 h-5" />;
      case 'MAINTENANCE_UPDATE':
      case 'MAINTENANCE_NEW':
      case 'MAINTENANCE_COMPLETED':
        return <Wrench className="w-5 h-5" />;
      case 'LEASE_RENEWAL':
      case 'LEASE_EXPIRY':
      case 'LEASE_NEW':
        return <FileText className="w-5 h-5" />;
      case 'INSPECTION':
      case 'INSPECTION_SCHEDULED':
      case 'INSPECTION_COMPLETED':
        return <ClipboardCheck className="w-5 h-5" />;
      case 'MESSAGE':
      case 'NEW_MESSAGE':
        return <MessageSquare className="w-5 h-5" />;
      case 'COMPLAINT':
      case 'COMPLAINT_UPDATE':
        return <AlertTriangle className="w-5 h-5" />;
      case 'PROPERTY':
      case 'PROPERTY_UPDATE':
        return <Home className="w-5 h-5" />;
      case 'DOCUMENT':
      case 'DOCUMENT_UPLOAD':
        return <FileText className="w-5 h-5" />;
      case 'CALENDAR':
      case 'REMINDER':
        return <Calendar className="w-5 h-5" />;
      case 'ALERT':
      case 'SLA_VIOLATION':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getNotificationColor = (type: string, priority: string) => {
    if (priority === 'URGENT' || priority === 'HIGH') {
      return 'bg-red-100 text-red-600';
    }
    switch (type?.toUpperCase()) {
      case 'PAYMENT_FAILED':
      case 'SLA_VIOLATION':
      case 'ALERT':
        return 'bg-red-100 text-red-600';
      case 'RENT_REMINDER':
      case 'PAYMENT_DUE':
      case 'LEASE_EXPIRY':
        return 'bg-orange-100 text-orange-600';
      case 'PAYMENT_RECEIVED':
      case 'MAINTENANCE_COMPLETED':
      case 'INSPECTION_COMPLETED':
        return 'bg-green-100 text-green-600';
      case 'MESSAGE':
      case 'NEW_MESSAGE':
        return 'bg-blue-100 text-blue-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getNavigationPath = (notification: AppNotification): string | null => {
    const role = user?.role?.toLowerCase() || 'renter';
    const { type, relatedEntityType, relatedEntityId, actionUrl } = notification;

    // Use actionUrl if provided
    if (actionUrl) {
      return actionUrl;
    }

    // Determine path based on notification type and related entity
    const entityType = relatedEntityType?.toUpperCase();
    const notifType = type?.toUpperCase();

    // Maintenance notifications
    if (notifType?.includes('MAINTENANCE') || entityType === 'MAINTENANCE') {
      if (role === 'renter') {
        return relatedEntityId ? `/maintenance/${relatedEntityId}` : '/maintenance/track';
      } else if (role === 'agent') {
        return relatedEntityId ? `/agent/maintenance/${relatedEntityId}` : '/agent/maintenance';
      }
      return '/maintenance/track';
    }

    // Payment notifications
    if (notifType?.includes('PAYMENT') || notifType?.includes('RENT') || entityType === 'PAYMENT') {
      if (role === 'renter') {
        return '/payment-history';
      } else if (role === 'owner') {
        return '/owner/ledger';
      }
      return '/payment-history';
    }

    // Lease notifications
    if (notifType?.includes('LEASE') || entityType === 'LEASE') {
      if (role === 'agent') {
        return relatedEntityId ? `/agent/leases/${relatedEntityId}` : '/agent/leases';
      } else if (role === 'owner') {
        return relatedEntityId ? `/owner/leases/${relatedEntityId}` : '/owner/leases';
      }
      return '/leases';
    }

    // Inspection notifications
    if (notifType?.includes('INSPECTION') || entityType === 'INSPECTION') {
      if (role === 'agent') {
        return relatedEntityId ? `/agent/inspections/${relatedEntityId}` : '/agent/inspections';
      }
      return '/notifications';
    }

    // Message notifications
    if (notifType?.includes('MESSAGE') || entityType === 'MESSAGE') {
      return '/messages';
    }

    // Property notifications
    if (notifType?.includes('PROPERTY') || entityType === 'PROPERTY') {
      if (role === 'agent') {
        return relatedEntityId ? `/agent/properties/${relatedEntityId}` : '/agent/properties';
      } else if (role === 'owner') {
        return relatedEntityId ? `/owner/properties/${relatedEntityId}` : '/owner/properties';
      }
      return '/properties';
    }

    // Complaint notifications
    if (notifType?.includes('COMPLAINT') || entityType === 'COMPLAINT') {
      return '/complaints';
    }

    // Document notifications
    if (notifType?.includes('DOCUMENT') || entityType === 'DOCUMENT') {
      return '/documents';
    }

    // Compliance notifications
    if (notifType?.includes('COMPLIANCE') || entityType === 'COMPLIANCE') {
      if (role === 'owner') {
        return relatedEntityId ? `/owner/compliance/${relatedEntityId}` : '/owner/compliance';
      }
      return '/owner/compliance';
    }

    // Job notifications for contractors
    if (notifType?.includes('JOB') || entityType === 'JOB') {
      if (role === 'contractor') {
        return '/contractor/jobs';
      }
    }

    return null;
  };

  const handleNotificationClick = async (notification: AppNotification) => {
    // Mark as read if not already
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    // Navigate to the relevant page
    const path = getNavigationPath(notification);
    if (path) {
      navigate(path);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  const filteredNotifications = filter === 'unread'
    ? notifications.filter(n => !n.isRead)
    : notifications;

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bell className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
            <p className="text-gray-600">
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
            </p>
          </div>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="flex items-center gap-2 px-4 py-2 text-secondary hover:bg-secondary/10 rounded-lg transition-colors"
          >
            <CheckCheck className="w-5 h-5" />
            Mark all as read
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'all'
              ? 'bg-primary text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'unread'
              ? 'bg-primary text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Unread {unreadCount > 0 && `(${unreadCount})`}
        </button>
      </div>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">
            {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map(notification => (
            <div
              key={notification.id}
              className={`bg-white rounded-lg shadow p-4 transition-all ${
                !notification.isRead ? 'border-l-4 border-secondary' : ''
              } ${getNavigationPath(notification) ? 'cursor-pointer hover:shadow-md' : ''}`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={`p-2 rounded-lg ${getNotificationColor(notification.type, notification.priority)}`}>
                  {getNotificationIcon(notification.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className={`font-medium ${!notification.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
                      {notification.title}
                    </h3>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {formatDate(notification.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{notification.message}</p>

                  {/* Action buttons */}
                  <div className="flex items-center gap-3 mt-3">
                    {notification.actionLabel && getNavigationPath(notification) && (
                      <span className="text-sm text-secondary font-medium">
                        {notification.actionLabel} →
                      </span>
                    )}
                    {!notification.actionLabel && getNavigationPath(notification) && (
                      <span className="text-sm text-secondary font-medium">
                        View details →
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {!notification.isRead && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notification.id);
                      }}
                      className="p-2 text-gray-400 hover:text-secondary hover:bg-gray-100 rounded-lg transition-colors"
                      title="Mark as read"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissNotification(notification.id);
                    }}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

